import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';
import ActionMenu from './ActionMenu';

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';


const dateKey = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

const waLink = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  if (!d) return null;
  return `https://wa.me/${d.length === 10 ? '91' + d : d}`;
};
const STATUS_STYLES = {
  Pending: 'bg-amber-50 text-amber-600',
  Completed: 'bg-green-50 text-green-600',
  Rescheduled: 'bg-purple-50 text-purple-600',
};

const STATUS_FILTER_OPTIONS = ['All', 'Pending', 'Completed', 'Rescheduled', 'No follow-up'];

const TYPE_FILTER_OPTIONS = ['All', 'Candidates', 'Employers'];

const GENDER_FILTER_OPTIONS = ['All', 'Male', 'Female', 'Other', 'Prefer not to say'];

function AgentFollowUps() {
  const [candidates, setCandidates] = useState([]);
  const [employers, setEmployers] = useState([]);
const [typeFilter, setTypeFilter] = useState('All');

  const [genderFilter, setGenderFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [todayOnly, setTodayOnly] = useState(false);

  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Which candidate row currently has its inline schedule form open
  const [activeRow, setActiveRow] = useState(null);
  const [rowForm, setRowForm] = useState({ scheduled_at: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState('');

  // ---- Search + status filter ----
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [profileUser, setProfileUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

    const [employerProfile, setEmployerProfile] = useState(null);
  const [employerLoading, setEmployerLoading] = useState(false);
  // ---- Bulk schedule ----
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkForm, setBulkForm] = useState({ scheduled_at: '', notes: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState('');

  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, typeFilter, genderFilter, dateFrom, dateTo, todayOnly, pageSize]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
     const [cRes, eRes, fRes] = await Promise.all([
  api.get('/agent/candidates'),
  api.get('/agent/employers'),
  api.get('/agent/follow-ups'),
]);
setCandidates(cRes.data);
setEmployers(eRes.data);
setFollowUps(fRes.data);
    } catch (err) {
      console.error('Failed to load follow-ups page', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Most recent follow-up (by scheduled_at) for a given candidate, if any
  const latestFollowUpFor = (candidateId) => {
    const matches = followUps.filter((f) => f.candidate_id === candidateId);
    if (matches.length === 0) return null;
    return matches.reduce((latest, f) =>
      new Date(f.scheduled_at) > new Date(latest.scheduled_at) ? f : latest
    );
  };

    // Candidates + employers merged into one list
  const people = useMemo(
    () => [
      ...candidates.map((c) => ({ ...c, type: 'candidate' })),
      ...employers.map((e) => ({ ...e, type: 'employer' })),
    ],
    [candidates, employers]
  );

  // Filtered candidate list — search by name/mobile, filter by follow-up status
  const filteredCandidates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
   return people.filter((c) => {
  if (typeFilter === 'Candidates' && c.type !== 'candidate') return false;
  if (typeFilter === 'Employers' && c.type !== 'employer') return false;

    if (genderFilter !== 'All') {
    if (c.type !== 'candidate') return false;
    if ((c.gender || '').toLowerCase() !== genderFilter.toLowerCase()) return false;
  }
  if (todayOnly || dateFrom || dateTo) {
    const todayKey = dateKey(new Date());
    const keys = followUps
      .filter((f) => f.candidate_id === c.id)
      .map((f) => dateKey(f.scheduled_at));
    if (todayOnly && !keys.includes(todayKey)) return false;
    if ((dateFrom || dateTo) && !keys.some((k) => (!dateFrom || k >= dateFrom) && (!dateTo || k <= dateTo))) return false;
  }

  if (q) {
        const nameMatch = (c.name || '').toLowerCase().includes(q);
        const phoneMatch = (c.phone || '').toLowerCase().includes(q);
        if (!nameMatch && !phoneMatch) return false;
      }
      if (statusFilter !== 'All') {
        const fu = latestFollowUpFor(c.id);
        if (statusFilter === 'No follow-up') {
          if (fu) return false;
        } else if (!fu || fu.status !== statusFilter) {
          return false;
        }
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, followUps, searchQuery, statusFilter, typeFilter, genderFilter, dateFrom, dateTo, todayOnly]);


    const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pagedCandidates = filteredCandidates.slice((curPage - 1) * pageSize, curPage * pageSize);

  const viewCandidateProfile = async (id) => {
    setProfileLoading(true);
    try {
      const res = await api.get(`/agent/candidates/${id}/profile`);
      setProfileUser(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load profile.');
    } finally {
      setProfileLoading(false);
    }
  };

    const viewEmployerProfile = async (id) => {
    setEmployerLoading(true);
    try {
      const res = await api.get(`/agent/employers/${id}`);
      setEmployerProfile(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load employer profile.');
    } finally {
      setEmployerLoading(false);
    }
  };

  const openScheduleRow = (candidateId) => {
    setActiveRow(candidateId);
    setRowForm({ scheduled_at: '', notes: '' });
    setRowError('');
  };

  const saveSchedule = async (candidateId) => {
    if (!rowForm.scheduled_at) {
      setRowError('Pick a date & time.');
      return;
    }
    setSaving(true);
    setRowError('');
    try {
      await api.post('/agent/follow-ups', {
        candidate_id: candidateId,
        scheduled_at: rowForm.scheduled_at,
        notes: rowForm.notes,
      });
      setActiveRow(null);
      loadData();
    } catch (err) {
      const data = err.response?.data;
      setRowError(data?.message || 'Could not schedule follow-up.');
    } finally {
      setSaving(false);
    }
  };

  const markStatus = async (id, status) => {
    try {
      await api.put(`/agent/follow-ups/${id}/status`, { status });
      loadData();
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  // ---- Selection helpers ----
  const toggleSelect = (candidateId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) next.delete(candidateId);
      else next.add(candidateId);
      return next;
    });
  };

  const allVisibleSelected =
    filteredCandidates.length > 0 && filteredCandidates.every((c) => selectedIds.has(c.id));

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        // deselect only the currently-visible ones, keep any others untouched
        const next = new Set(prev);
        filteredCandidates.forEach((c) => next.delete(c.id));
        return next;
      }
      const next = new Set(prev);
      filteredCandidates.forEach((c) => next.add(c.id));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkForm({ scheduled_at: '', notes: '' });
    setBulkError('');
  };

  const saveBulkSchedule = async () => {
    if (!bulkForm.scheduled_at) {
      setBulkError('Pick a date & time.');
      return;
    }
    setBulkSaving(true);
    setBulkError('');
    try {
      await Promise.all(
        Array.from(selectedIds).map((candidateId) =>
          api.post('/agent/follow-ups', {
            candidate_id: candidateId,
            scheduled_at: bulkForm.scheduled_at,
            notes: bulkForm.notes,
          })
        )
      );
      clearSelection();
      loadData();
    } catch (err) {
      console.error('Bulk schedule failed', err);
      setBulkError('Could not schedule follow-ups for one or more candidates.');
    } finally {
      setBulkSaving(false);
    }
  };

    const hasActiveFilters =
    searchQuery || statusFilter !== 'All' || typeFilter !== 'All' ||
    genderFilter !== 'All' || dateFrom || dateTo || todayOnly;

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('All');
    setTypeFilter('All');
    setGenderFilter('All');
    setDateFrom('');
    setDateTo('');
    setTodayOnly(false);
  };

  // After
return (
  <AgentLayout>
    <div className="-m-4 sm:-m-6 p-4 sm:p-6 min-h-[calc(100vh-57px)] bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">Follow-ups</h2>
      <p className="text-gray-500 text-sm mb-6">Schedule and manage follow-ups per candidate.</p>

          {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or mobile…"
              className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTodayOnly((v) => !v)}
              className={
                todayOnly
                  ? 'px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm'
                  : 'px-4 py-2.5 rounded-xl text-sm font-semibold bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
              }
            >
              📅 Today's Follow-ups
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition"
              >
                ✕ Clear filters
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            >
              {TYPE_FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Gender</label>
            <select
              value={genderFilter}
              onChange={(e) => setGenderFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            >
              {GENDER_FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt === 'All' ? 'All genders' : opt}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Follow-up From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Follow-up To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Candidate profile popup */}
      {profileUser && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => setProfileUser(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setProfileUser(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 text-xl font-bold"
              aria-label="Close"
            >
              ×
            </button>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                {profileUser.profile_photo_path && (
                  <img
                    src={`${BACKEND_URL}/storage/${profileUser.profile_photo_path}`}
                    alt={profileUser.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                )}
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{profileUser.name}</h3>
                  <p className="text-gray-500 text-sm">{profileUser.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <p><span className="font-medium">Phone:</span> {profileUser.phone || '—'}</p>
                <p><span className="font-medium">DOB:</span> {profileUser.date_of_birth || '—'}</p>
                <p><span className="font-medium">Gender:</span> {profileUser.gender || '—'}</p>
                <p><span className="font-medium">City:</span> {profileUser.city || '—'}</p>
                <p><span className="font-medium">State:</span> {profileUser.state || '—'}</p>
                <p><span className="font-medium">Pincode:</span> {profileUser.pincode || '—'}</p>
                <p className="col-span-2"><span className="font-medium">Address:</span> {profileUser.address || '—'}</p>
              </div>

              {profileUser.career_objective && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Career Objective:</span> {profileUser.career_objective}
                </p>
              )}

              {profileUser.resume_path && (
                <a
                  href={`${BACKEND_URL}/storage/${profileUser.resume_path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-purple-600 hover:underline text-sm font-medium"
                >
                  View Resume
                </a>
              )}

              {profileUser.skills?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {profileUser.skills.map((s) => (
                      <span key={s.id} className="bg-purple-50 text-purple-600 text-xs font-medium px-2 py-1 rounded">
                        {s.skill_name || s.name || s.skill || s.title} {s.proficiency ? `(${s.proficiency})` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profileUser.educations?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Education</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.educations.map((e) => (
                      <li key={e.id}>
                        {e.degree} — {e.institution} {e.year_of_passing ? `(${e.year_of_passing})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {profileUser.experiences?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Experience</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.experiences.map((exp) => (
                      <li key={exp.id}>{exp.job_title} — {exp.company_name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {profileUser.job_applications?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Job Applications</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.job_applications.map((app) => (
                      <li key={app.id} className="border-b pb-1">
                        <span className="font-medium">{app.job_posting?.title}</span> at {app.job_posting?.company_name} — <span className="capitalize">{app.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

            {/* Employer profile popup */}
      {employerProfile && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
          onClick={() => setEmployerProfile(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setEmployerProfile(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 text-xl font-bold"
              aria-label="Close"
            >
              ×
            </button>

            <div className="space-y-5">
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{employerProfile.name}</h3>
                <p className="text-gray-500 text-sm">{employerProfile.email}</p>
              </div>

              <div className="flex gap-2">
                {employerProfile.phone && (
                  <a
                    href={`tel:${employerProfile.phone}`}
                    className="px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white hover:opacity-90"
                  >
                    Call
                  </a>
                )}
                {waLink(employerProfile.phone) && (
                  <a
                    href={waLink(employerProfile.phone)}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-full text-sm font-semibold bg-green-500 text-white hover:opacity-90"
                  >
                    WhatsApp
                  </a>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <p><span className="font-medium">Phone:</span> {employerProfile.phone || '—'}</p>
                <p><span className="font-medium">City:</span> {employerProfile.city || '—'}</p>
                <p><span className="font-medium">State:</span> {employerProfile.state || '—'}</p>
                <p><span className="font-medium">Industry:</span> {employerProfile.company_industry || '—'}</p>
                <p><span className="font-medium">Company Size:</span> {employerProfile.company_size || '—'}</p>
                <p><span className="font-medium">Website:</span> {employerProfile.company_website || '—'}</p>
                <p className="col-span-2"><span className="font-medium">Address:</span> {employerProfile.address || '—'}</p>
              </div>

              {employerProfile.company_about && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">About:</span> {employerProfile.company_about}
                </p>
              )}

              {employerProfile.jobs?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Jobs ({employerProfile.jobs.length})</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {employerProfile.jobs.map((j) => (
                      <li key={j.id} className="border-b pb-1">
                        <span className="font-medium">{j.title}</span>
                        {j.location ? ` — ${j.location}` : ''} — <span className="capitalize">{j.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {employerProfile.candidates?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Your Candidates Here</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {employerProfile.candidates.map((cd) => (
                      <li key={cd.id} className="border-b pb-1">
                        <span className="font-medium">{cd.name}</span> — {cd.role} — <span className="capitalize">{cd.stage}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk schedule bar */}
      {selectedIds.size > 0 && (
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-4">
          <div className="flex flex-wrap items-end gap-3">
            <p className="text-xs font-semibold text-gray-600 w-full sm:w-auto">
              {selectedIds.size} candidate{selectedIds.size === 1 ? '' : 's'} selected
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time</label>
              <input
                type="datetime-local"
                value={bulkForm.scheduled_at}
                onChange={(e) => setBulkForm({ ...bulkForm, scheduled_at: e.target.value })}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <input
                type="text"
                value={bulkForm.notes}
                onChange={(e) => setBulkForm({ ...bulkForm, notes: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
            </div>
            <button
              onClick={saveBulkSchedule}
              disabled={bulkSaving}
              className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-sm disabled:opacity-50 transition"
            >
              {bulkSaving ? 'Scheduling...' : `Schedule for ${selectedIds.size}`}
            </button>
            <button
              onClick={clearSelection}
              disabled={bulkSaving}
              className="text-sm text-gray-500 hover:underline px-2 py-2"
            >
              Cancel
            </button>
          </div>
          {bulkError && <p className="text-red-500 text-xs mt-2">{bulkError}</p>}
        </div>
      )}

            <div className="flex items-center justify-between flex-wrap gap-2 mb-2 text-sm text-gray-600">
        <span className="font-semibold">
          Showing {filteredCandidates.length === 0 ? 0 : (curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, filteredCandidates.length)} of {filteredCandidates.length}
        </span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="border border-purple-200 rounded-full px-2 py-1 text-xs bg-white"
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 text-[11px] uppercase tracking-wide border-b border-gray-100 bg-gray-50">
              <th className="px-5 py-3 font-medium w-10">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  className="accent-purple-600"
                />
              </th>
              <th className="px-5 py-3 font-medium">Name</th>
<th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium">Mobile</th>
              <th className="px-5 py-3 font-medium whitespace-nowrap">Next / Last Follow-up</th>
              <th className="px-5 py-3 font-medium">Status</th>
<th className="px-3 py-3 font-medium w-[140px] min-w-[140px] sticky right-0 z-10 bg-gray-50">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="py-6 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={7} className="py-6 text-center text-red-500">
                Could not load data. <button onClick={() => loadData()} className="underline">Retry</button>
              </td></tr>
            )}
            {!loading && !loadError && people.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-gray-400">No candidates yet.</td></tr>
            )}
            {!loading && !loadError && people.length > 0 && filteredCandidates.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-gray-400">No candidates match your search/filter.</td></tr>
            )}
            {!loading && !loadError && pagedCandidates.map((c) => {
              const fu = latestFollowUpFor(c.id);
              const isOpen = activeRow === c.id;
              const isSelected = selectedIds.has(c.id);

              return (
                <React.Fragment key={c.id}>
                  <tr className={`border-t border-gray-50 hover:bg-purple-50/30 transition-colors ${isSelected ? 'bg-purple-50/50' : ''}`}>
                    <td className="px-5 py-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(c.id)}
                        className="accent-purple-600"
                      />
                    </td>
                                      <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${c.type === 'employer' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                          {(c.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <button
                          type="button"
                          onClick={() => (c.type === 'candidate' ? viewCandidateProfile(c.id) : viewEmployerProfile(c.id))}
                          disabled={profileLoading || employerLoading}
                          className="font-medium text-left text-[#7C3AED] hover:underline disabled:opacity-60"
                        >
                          {c.name}
                        </button>
                      </div>
                    </td>
<td className="px-5 py-3">
  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.type === 'employer' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>
    {c.type === 'employer' ? 'Employer' : 'Candidate'}
  </span>
</td>
                    <td className="px-5 py-3">{c.phone || '—'}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{fu ? fmtDateTime(fu.scheduled_at) : '—'}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {fu ? (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[fu.status] || 'bg-gray-50 text-gray-500'}`}>
                          {fu.status}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">No follow-up yet</span>
                      )}
                    </td>
<td className="px-3 py-3 whitespace-nowrap w-[140px] min-w-[140px] sticky right-0 z-10 bg-white shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.1)]">
  <ActionMenu
    phone={c.phone}
    items={[
      !fu || fu.status === 'Completed'
        ? { label: 'Mark Done', icon: '✓', tone: 'off', disabled: true }
        : { label: 'Mark Done', icon: '✓', tone: 'green', onClick: () => markStatus(fu.id, 'Completed') },
      {
        label: isOpen ? 'Cancel schedule' : fu ? 'Reschedule' : 'Schedule',
        icon: '📅',
        tone: isOpen ? 'gray' : 'purple',
        onClick: () => (isOpen ? setActiveRow(null) : openScheduleRow(c.id)),
      },
    ]}
  />
</td>
                  </tr>

                  {isOpen && (
                    <tr className="bg-purple-50/40 border-t border-gray-50">
                      <td colSpan={7} className="px-5 py-4">
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time</label>
                            <input
                              type="datetime-local"
                              value={rowForm.scheduled_at}
                              onChange={(e) => setRowForm({ ...rowForm, scheduled_at: e.target.value })}
                              className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                            />
                          </div>
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                            <input
                              type="text"
                              value={rowForm.notes}
                              onChange={(e) => setRowForm({ ...rowForm, notes: e.target.value })}
                              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                            />
                          </div>
                          <button
                            onClick={() => saveSchedule(c.id)}
                            disabled={saving}
                          
className="bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-sm disabled:opacity-50 transition"
                          >
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                        {rowError && <p className="text-red-500 text-xs mt-2">{rowError}</p>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {(() => {
        const total = filteredCandidates.length;
        const from = total === 0 ? 0 : (curPage - 1) * pageSize + 1;
        const to = Math.min(curPage * pageSize, total);
        const nums = [];
        for (let p = Math.max(1, curPage - 2); p <= Math.min(totalPages, curPage + 2); p++) nums.push(p);
        const btn = 'px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-40';
        return (
          <div className="flex items-center justify-between flex-wrap gap-3 mt-3 text-sm text-gray-600">
            <span>Showing {from}–{to} of {total}</span>
            <div className="flex items-center gap-1">
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(1)}>«</button>
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>Prev</button>
              {nums.map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`${btn} ${p === curPage ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent' : 'bg-white border-purple-200'}`}
                >
                  {p}
                </button>
              ))}
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(curPage + 1)}>Next</button>
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          </div>
        );
      })()}
      </div>
    </AgentLayout>
  );
}

export default AgentFollowUps;